import { DataStore } from "aws-amplify/datastore";
import { getCurrentUser } from "aws-amplify/auth";
import { generateClient } from "aws-amplify/api";
import { Invoice, ServiceRendered } from "../../models";
import { CreateInvoicesMutation, SendInvoicesMutation } from "../../API";
import { createInvoices, sendInvoices } from "../../graphql/mutations";
import { InvoiceRepository } from "../interfaces";
import { InvoiceRecord } from "../types";

export class DatastoreInvoiceRepository implements InvoiceRepository {
  async create(startDate: Date, endDate: Date, clientIds: string[], label: string): Promise<void> {
    const user = await getCurrentUser();
    const apiClient = generateClient();
    await apiClient.graphql({
      query: createInvoices,
      variables: {
        clientIds,
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        userID: user.userId,
        label,
      },
    }) as { data: CreateInvoicesMutation };
  }

  async send(invoiceIDs: string[], message: string, subject: string): Promise<void> {
    const apiClient = generateClient();
    await apiClient.graphql({
      query: sendInvoices,
      variables: { invoiceIDs, messageText: message, subject },
    }) as { data: SendInvoicesMutation };
  }

  async delete(id: string): Promise<void> {
    const servicesRendered = await DataStore.query(ServiceRendered);
    for (const s of servicesRendered) {
      if (s.invoiceID === id) {
        await DataStore.save(
          ServiceRendered.copyOf(s, (updated) => {
            Object.assign(updated, { ...s, invoiceID: null });
          })
        );
      }
    }
    await DataStore.delete(Invoice, id);
  }

  async update(invoice: Partial<InvoiceRecord> & { id: string }): Promise<InvoiceRecord> {
    const original = await DataStore.query(Invoice, invoice.id);
    if (!original) throw new Error("Invoice not found to update");
    const updated = await DataStore.save(
      Invoice.copyOf(original, (draft) => {
        Object.assign(draft, invoice);
      })
    );
    return {
      id: updated.id,
      clientID: updated.clientID,
      invoiceName: updated.invoiceName,
      filePath: updated.filePath,
      startDate: updated.startDate,
      endDate: updated.endDate,
      subtotal: updated.subtotal,
      owner: updated.owner,
      label: updated.label,
      status: updated.status ?? null,
      paidAt: updated.paidAt ?? null,
      servicesRendered: [],
    };
  }

  async cancel(invoice: InvoiceRecord): Promise<InvoiceRecord> {
    const servicesRendered = await DataStore.query(ServiceRendered);
    for (const s of servicesRendered) {
      if (s.invoiceID === invoice.id) {
        await DataStore.save(
          ServiceRendered.copyOf(s, (updated) => {
            Object.assign(updated, { ...s, invoiceID: null });
          })
        );
      }
    }
    return this.update(invoice);
  }
}

function formatDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}
